import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal } from '@mui/material';
import moment from 'moment';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { GlobalContext } from '../../providers/GlobalProvider';
import { ExportHubAPI, LicenseOverview as Overview } from 'api/ExportHubAPI';
import { KpiTile, KpiGroup } from './kpiTiles';
import { formatCompactCurrency, formatCurrency } from './licensePresets';
import { LicenseTypeChart } from './dashboardCharts';
import ChoroplethMap from './ChoroplethMap';
import StyledSingleSelect from './StyledSingleSelect';
import { buildCountryValueMap, toAtlasCountry } from './mapCountries';
import LicenseRequestWizard from './LicenseRequestWizard';

const EXPIRY_WINDOWS = [30, 60, 90] as const;
type ExpiryWindow = (typeof EXPIRY_WINDOWS)[number];

const LicenseOverview: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [expiryWindow, setExpiryWindow] = useState<ExpiryWindow>(30);
  const [focusCountry, setFocusCountry] = useState<string | null>(null);
  const [databases, setDatabases] = useState<Array<{ id: string; name: string }>>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reqDbId, setReqDbId] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [ov, facets] = await Promise.all([
        ExportHubAPI.getLicenseOverview(groupId),
        ExportHubAPI.getOverviewFacets(groupId),
      ]);
      setData(ov);
      setDatabases(facets.databases);
    } catch (err) {
      console.error('Failed to load license overview:', err);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className='export-hub-panel'>
        <div className='eh-table-skeleton'>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className='export-hub-panel'>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>License Overview unavailable</div>
        </div>
      </div>
    );
  }

  const { kpis } = data;
  const expiringCount =
    expiryWindow === 30
      ? kpis.expiring_30
      : expiryWindow === 60
      ? kpis.expiring_60
      : kpis.expiring_90;
  const valueMap = buildCountryValueMap(data.by_country, (r) => r.count);
  const remainingMap = buildCountryValueMap(
    data.by_country,
    (r) => r.value_remaining || 0,
  );
  const topCountries = [...data.by_country].slice(0, 10);
  const maxTop = topCountries[0]?.count || 1;
  const expiring = data.expiring_licenses.filter(
    (l) => l.days_left <= expiryWindow,
  );

  return (
    <>
      <div className='export-hub-section-header'>
        <div>
          <h2 className='export-hub-section-title'>License Overview</h2>
          <p className='export-hub-section-sub'>
            Every export license across your databases — geography, type, ECCN
            coverage, and expirations.
          </p>
        </div>
        <div className='export-hub-header-actions'>
          <button
            className='btn-primary'
            type='button'
            onClick={() => {
              setReqDbId(databases[0]?.id || '');
              setPickerOpen(true);
            }}
          >
            <AddIcon style={{ fontSize: 18 }} /> Add License
          </button>
        </div>
      </div>

      <div className='eh-dash'>
      <div className='eh-kpi-groups eh-kpi-overview'>
        <KpiGroup>
          <KpiTile label='Active Licenses' value={kpis.active_licenses} tone='info' />
          <KpiTile label='Active Countries' value={kpis.active_countries} />
          <KpiTile
            label='Authorized Value'
            value={formatCompactCurrency(kpis.authorized_value)}
          />
          <KpiTile
            label='Value Remaining'
            value={formatCompactCurrency(kpis.value_remaining)}
            tone='success'
            hint={
              kpis.authorized_value > 0
                ? `${Math.round(
                    (kpis.consumed_value / kpis.authorized_value) * 100,
                  )}% consumed`
                : undefined
            }
          />
          {/* Licenses at 90%+ of their authorized value. Over-drawn ones are
              called out separately because they need action, not attention. */}
          <KpiTile
            label={kpis.over_drawn > 0 ? 'Over-drawn' : 'Nearly Exhausted'}
            value={
              kpis.over_drawn > 0 ? kpis.over_drawn : kpis.nearly_exhausted
            }
            tone={kpis.over_drawn > 0 ? 'danger' : 'warning'}
            hint={
              kpis.over_drawn > 0
                ? `${kpis.nearly_exhausted} at 90%+`
                : 'of authorized value'
            }
          />
          <KpiTile
            label={`Expiring (${expiryWindow}d)`}
            value={expiringCount}
            tone='warning'
            hint='Toggle window below'
          />
        </KpiGroup>
        <div className='eh-expiry-toggle'>
          {EXPIRY_WINDOWS.map((w) => (
            <button
              key={w}
              type='button'
              className={`eh-seg-btn ${expiryWindow === w ? 'active' : ''}`}
              onClick={() => setExpiryWindow(w)}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {/* Geographic + top countries */}
      <div className='eh-dash-geo'>
        <div className='eh-panel'>
          <div className='eh-panel-title eh-panel-title-flex'>
            <span>
              <PublicOutlinedIcon
                style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
              />
              Geographic Distribution
            </span>
            {focusCountry && (
              <button
                type='button'
                className='eh-focus-chip'
                onClick={() => setFocusCountry(null)}
              >
                {focusCountry}
                <CloseIcon style={{ fontSize: 13 }} />
              </button>
            )}
          </div>
          <ChoroplethMap
            values={valueMap}
            selected={focusCountry ? [toAtlasCountry(focusCountry)] : []}
            tooltipFor={(c, v) => {
              const rem = remainingMap.get(c) || 0;
              return `${v} license${v === 1 ? '' : 's'} · ${formatCompactCurrency(
                rem,
              )} left`;
            }}
            onCountryClick={(atlas) => {
              const row = data.by_country.find(
                (r) => toAtlasCountry(r.country) === atlas,
              );
              setFocusCountry(row ? row.country : null);
            }}
          />
        </div>
        <div className='eh-panel'>
          <div className='eh-panel-title'>Top Countries</div>
          {topCountries.length === 0 && (
            <div className='eh-detail-note'>No license geography yet.</div>
          )}
          {topCountries.map((c) => (
            <button
              key={c.country}
              type='button'
              className={`eh-topctry ${focusCountry === c.country ? 'active' : ''}`}
              onClick={() =>
                setFocusCountry(focusCountry === c.country ? null : c.country)
              }
            >
              <span className='eh-topctry-name'>{c.country}</span>
              <span className='eh-topctry-bar'>
                <span
                  className='eh-topctry-fill'
                  style={{ width: `${Math.max(6, (c.count / maxTop) * 100)}%` }}
                />
              </span>
              <span className='eh-topctry-count'>
                {c.count} · {formatCompactCurrency(c.value_remaining || 0)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Type + agency */}
      <div className='eh-dash-band'>
        <div className='eh-panel'>
          <div className='eh-panel-title'>Active Licenses by Type</div>
          <LicenseTypeChart data={data.by_type} />
        </div>
        <div className='eh-panel'>
          <div className='eh-panel-title'>By Agency</div>
          {data.by_agency.length === 0 && (
            <div className='eh-detail-note'>No licenses yet.</div>
          )}
          {data.by_agency.map((a) => (
            <div className='eh-agency-row' key={a.agency}>
              <span className='eh-agency-name'>{a.agency}</span>
              <span className='eh-agency-count'>{a.count}</span>
            </div>
          ))}
        </div>
        <div className='eh-panel'>
          <div className='eh-panel-title'>Value Remaining</div>
          <div className='eh-bigstat'>
            {formatCompactCurrency(kpis.value_remaining)}
          </div>
          <div className='eh-bigstat-sub'>
            of {formatCompactCurrency(kpis.authorized_value)} authorized
          </div>
          <div className='eh-value-bar'>
            <span
              className='eh-value-bar-fill'
              style={{
                width: `${
                  kpis.authorized_value > 0
                    ? Math.min(
                        100,
                        (kpis.value_remaining / kpis.authorized_value) * 100,
                      )
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* ECCN coverage + expirations */}
      <div className='eh-dash-band'>
        <div className='eh-panel eh-dash-span2'>
          <div className='eh-panel-title'>ECCN Coverage</div>
          {data.eccn_coverage.length === 0 ? (
            <div className='eh-detail-note'>No ECCN-bearing licenses yet.</div>
          ) : (
            <table className='runs-table'>
              <thead>
                <tr>
                  <th>ECCN</th>
                  <th className='eh-num'>Licenses</th>
                  <th className='eh-num'>Countries</th>
                  <th className='eh-num'>Value Authorized</th>
                </tr>
              </thead>
              <tbody>
                {data.eccn_coverage.map((e) => (
                  <tr key={e.eccn} className='hoverable-row'>
                    <td>
                      <span className='eh-eccn-chip'>{e.eccn}</span>
                    </td>
                    <td className='eh-num'>{e.license_count}</td>
                    <td className='eh-num'>{e.country_count}</td>
                    <td className='eh-num'>{formatCurrency(e.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className='eh-panel'>
          <div className='eh-panel-title'>Upcoming Expirations ({expiryWindow}d)</div>
          {expiring.length === 0 && (
            <div className='eh-detail-note'>
              No licenses expiring within {expiryWindow} days.
            </div>
          )}
          {expiring.map((l) => (
            <div className='eh-expiry-row' key={l.id}>
              <div className='eh-expiry-info'>
                <span className='eh-expiry-title'>
                  {l.license_number || l.license_type}
                </span>
                <span className='eh-expiry-meta'>
                  {l.license_type} ·{' '}
                  {l.expiry_date
                    ? moment(l.expiry_date).format('MM/DD/YYYY')
                    : '—'}
                </span>
              </div>
              <span
                className={l.days_left <= 30 ? 'eh-days-urgent' : 'eh-days'}
              >
                {l.days_left}d
              </span>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* Add License — pick a database first (unscoped overview, §5.3). */}
      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        className='eh-modal-root'
      >
        <div className='eh-modal eh-modal-sm' role='dialog' aria-label='Choose database'>
          <div className='eh-modal-head'>
            <h2 className='eh-modal-title'>Add License</h2>
            <button
              className='eh-modal-close'
              onClick={() => setPickerOpen(false)}
              aria-label='Close'
              type='button'
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>
          <div className='eh-modal-body'>
            {databases.length === 0 ? (
              <div className='export-hub-empty' style={{ padding: '24px 8px' }}>
                <div className='export-hub-empty-title'>No databases yet</div>
                <div className='export-hub-empty-sub'>
                  Licenses are tracked inside a database. Create one first,
                  then add a license to it.
                </div>
              </div>
            ) : (
              <div className='eh-field'>
                <span className='eh-field-label'>Database</span>
                <StyledSingleSelect
                  options={databases.map((d) => ({
                    value: d.id,
                    label: d.name,
                  }))}
                  value={reqDbId}
                  onChange={setReqDbId}
                  inline
                />
                <span className='eh-field-hint'>
                  The license inherits the chosen database&rsquo;s scope.
                </span>
              </div>
            )}
          </div>
          <div className='eh-modal-foot'>
            <button
              className='btn-outline'
              type='button'
              onClick={() => setPickerOpen(false)}
            >
              Cancel
            </button>
            {databases.length === 0 ? (
              <button
                className='btn-primary'
                type='button'
                onClick={() => {
                  setPickerOpen(false);
                  history.push('/export-hub/databases');
                }}
              >
                Create Database
              </button>
            ) : (
              <button
                className='btn-primary'
                type='button'
                onClick={() => {
                  setPickerOpen(false);
                  setWizardOpen(true);
                }}
                disabled={!reqDbId}
                style={{ opacity: reqDbId ? 1 : 0.6 }}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </Modal>

      {reqDbId && (
        <LicenseRequestWizard
          open={wizardOpen}
          databaseId={reqDbId}
          groupId={groupId}
          onClose={() => setWizardOpen(false)}
          onCreated={() => {
            setWizardOpen(false);
            load();
          }}
        />
      )}
    </>
  );
};

export default LicenseOverview;
